import { Alert, Button, Divider, Form, Modal } from "antd";
import { Rule } from "antd/lib/form";
import useBulkCardUpdate, { BulkUpdateResult } from "hooks/useBulkCardUpdate";
import { Owners } from "models/applicationUsers";
import { PageRequest } from "models/common";
import React, { useState } from "react";
import { ownershipValidator } from "util/ownership";
import OwnershipSelector from "../OwnershipSelector";

export interface BulkEditProps {
    visible: boolean;
    title?: string;
    affectedCount: number;
    silentAccounts: boolean;
    multipleOwners: boolean;
    onClose: () => void;
    onSubmit?: (form: { owners: Owners }) => Promise<BulkUpdateResult>;
    onSuccess?: () => void;
}

const BulkEditModal = (props: BulkEditProps) => {
    const [lockedCount, setLockedCount] = useState<number | undefined>();
    const [working, setWorking] = useState<boolean>(false);
    const [updateResult, setUpdateResult] = useState<
        BulkUpdateResult | undefined
    >(undefined);
    const [error, setError] = useState<string>();
    const [form] = Form.useForm();

    const closeModal = () => {
        props.onClose();
        setUpdateResult(undefined);
        setLockedCount(undefined);
        form.resetFields();
    };

    const submit = async (form: { owners: Owners }) => {
        setWorking(true);
        let result: BulkUpdateResult | undefined;

        try {
            // locking in the affected count so it doesn't change
            // if the update causes the filter result to change.
            setLockedCount(props.affectedCount);
            result = props.onSubmit && (await props.onSubmit(form));
        } finally {
            setWorking(false);
        }

        if (result) {
            setUpdateResult(result);
            props.onSuccess && props.onSuccess();
        }
    };

    const showResponseAlert = (updateCount: number) => {
        if (updateCount === 0) {
            return (
                <Alert
                    className="bold-font"
                    message="No records updated, you may have set the same customer!"
                    type="warning"
                    showIcon
                />
            );
        } else {
            return (
                <Alert
                    className="bold-font"
                    message={`${updateCount} records updated successfully!`}
                    type="success"
                    showIcon
                />
            );
        }
    };

    const validateOwnership = async (_: Rule, owners: Owners) => {
        await ownershipValidator(props.silentAccounts, owners);
    }

    return (
        <Modal
            className="card-edit-modal"
            title={props.title}
            open={props.visible}
            closable={false}
            keyboard={false}
            maskClosable={false}
            destroyOnClose
            confirmLoading={working}
            cancelButtonProps={{ disabled: working }}
            okText={working ? "Saving..." : "Update"}
            okButtonProps={{ form: "bulk-edit-form", htmlType: "submit" }}
            onCancel={closeModal}
            footer={
                updateResult ? (
                    <Button type="primary" onClick={closeModal}>
                        OK
                    </Button>
                ) : undefined
            }
        >
            <Form
                id="bulk-edit-form"
                form={form}
                layout="vertical"
                disabled={working || (updateResult?.updateCount ?? 0) > 0}
                onFinish={submit}
            >
                <Form.Item
                    name="owners"
                    help={error && <Alert showIcon type="error" message={error} />}
                    rules={[
                        { validator: validateOwnership }
                    ]}
                >
                    <OwnershipSelector
                        supportSilentAccounts={props.silentAccounts}
                        allowMultiple={props.multipleOwners}
                        onError={setError}
                    />
                </Form.Item>
            </Form>
            <Alert
                className="bold-font"
                message={`This update can affect up to ${(
                    lockedCount || props.affectedCount
                ).toLocaleString()} cards!`}
                type="info"
                showIcon
            />
            {updateResult && (
                <>
                    <Divider />
                    {showResponseAlert(updateResult.updateCount)}
                </>
            )}
        </Modal>
    );
};

export const BulkEditSelectionModal = (
    props: BulkEditProps & { cardIds: React.Key[] }
) => {
    const { updateBySelection } = useBulkCardUpdate();

    const onSubmit = async (form: { owners: Owners }) => {
        return await updateBySelection({ ...form, cardIds: props.cardIds });
    };

    return (
        <BulkEditModal
            {...props}
            title="Bulk Edit Selected Cards"
            onSubmit={onSubmit}
        />
    );
};

export const BulkEditFilterModal = (
    props: BulkEditProps & { pageRequest: PageRequest }
) => {
    const { updateByFilter } = useBulkCardUpdate();

    const onSubmit = async (form: { owners: Owners }) => {
        return await updateByFilter({
            ...form,
            pageRequest: props.pageRequest,
        });
    };

    return (
        <BulkEditModal
            {...props}
            title="Bulk Edit Filtered Cards"
            onSubmit={onSubmit}
        />
    );
};
